
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { fetchContactRelationshipHealth } from "@/services/relationship/relationshipHealthService";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { formatDate } from "@/lib/utils";
import { TrendingUp, TrendingDown, Minus, CalendarDays, MessageSquare } from "lucide-react";

interface RelationshipHealthCardProps {
  contactId: string;
}

export function RelationshipHealthCard({ contactId }: RelationshipHealthCardProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["relationshipHealth", contactId],
    queryFn: () => fetchContactRelationshipHealth(contactId),
    enabled: !!contactId,
  });

  const getSentimentIcon = (sentiment: string | undefined) => {
    switch (sentiment) {
      case "positive":
        return <TrendingUp className="h-5 w-5 text-green-500" />;
      case "negative":
        return <TrendingDown className="h-5 w-5 text-red-500" />;
      default:
        return <Minus className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return "bg-green-500";
    if (score >= 40) return "bg-yellow-500";
    return "bg-red-500";
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Relationship Health</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[150px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Relationship Health</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Failed to load relationship health data
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Relationship Health</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Health Score</p>
            <div className="flex items-center gap-2 mt-1">
              {getSentimentIcon(data?.sentiment)}
              <span className="text-2xl font-bold">{data?.health_score || 0}</span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium">Sentiment</p>
            <p className="text-sm capitalize">{data?.sentiment || "neutral"}</p>
          </div>
        </div>

        <div>
          <div className="flex justify-between mb-1">
            <span className="text-sm">Poor</span>
            <span className="text-sm">Excellent</span>
          </div>
          <Progress 
            value={data?.health_score || 0} 
            className={`h-2 ${getScoreColor(data?.health_score || 0)}`} 
          />
        </div>

        <div className="grid grid-cols-2 gap-4 pt-2">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Last Assessment</span>
            </div>
            <p className="text-sm font-medium">
              {data?.last_assessment_date
                ? formatDate(data.last_assessment_date)
                : "No assessment"}
            </p>
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Opportunities</span>
            </div>
            <p className="text-sm font-medium">{data?.opportunities_count || 0}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
